import React, { useEffect, useState } from "react";
import { ColumnHelper } from "util/TableHelper";
import {
  Empty,
  Col,
  Row,
  Card,
  List,
  Radio,
  Form,
  Tooltip,
  Collapse,
  InputNumber,
} from "antd";
import CardLogo from "./CardLogo";
import moment from "moment";
import B2BFileModal from "./B2BFileModal";
import { useTranslation } from "react-i18next";

const PosSelectableInstallmentTable = (props) => {
  const { useCommissionsForAvailableInstallment } = props.data;
  const [selectedValue, setSelectedValue] = useState(null);
  const { t } = useTranslation();
  const [installmentOptionsCount, setInstallmentOptionsCount] = useState(0);
  const [justOneInstallmentOptionsCount, setJustOneInstallmentOptionsCount] =
    useState(0);
  const [selectedRadioKey, setSelectedRadioKey] = useState(null);

  const isCommissionDisabled = (commissionId) => {
    if (props.disabledOptions === true) return true;
    if (
      props.enabledCommissionIds != null &&
      !props.enabledCommissionIds.includes(commissionId)
    )
      return true;
    return false;
  };

  const calculatePayableAmount = (
    amount,
    commissionRate,
    bankId,
    isCommissionFromDealer
  ) => {
    let payableAmount = amount;
    if (useCommissionsForAvailableInstallment) {
      payableAmount = amount + (amount / 100) * commissionRate;
    }

    if (isCommissionFromDealer && useCommissionsForAvailableInstallment) {
      payableAmount = (amount / (100 - commissionRate)) * 100;
    }

    if (bankId == 856) {
      payableAmount = amount / ((100 - commissionRate) / 100);
    }
    return payableAmount;
  };

  const onChangeRadio = (e) => {
    const radioValue = e.target.value;
    setSelectedRadioKey(radioValue);
    if (props.onChange) {
      props.onChange(e.target.value, {
        bankId: e.target.bankId,
        installmentCount: e.target.installmentCount,
        cardType: e.target.cardType,
      });
    }

    setSelectedValue(e.target.value);
  };

  const handleDeferringInstallmentChange = (e) => {
    const newVal = e;
    // Sadece erteleme taksit sayısını gönder
    props.onChange(selectedRadioKey, {
      deferringInstallmentCount: newVal,
    });
  };

  const renderList = (commissions, justOneInstallment = false) => {
    let rates = Object.keys(commissions).filter(
      (x) => x.startsWith("commissionRate") && !x.endsWith("Enabled")
    );

    return (
      <List size="small">
        {rates.map((rate, i) => {
          const commissionRate = commissions[rate];
          if (!commissionRate && commissionRate !== 0) return null;
          if (justOneInstallment && i !== 0) return null;
          if (!justOneInstallment && i === 0) return null;

          let isCommissionIncluded =
            commissions[`isCommissionIncludedCommissionRate${i + 1}`];

          const totalInstallment =
            i === 0 ? 1 : i + 1 + commissions.extraInstallmentCount;

          let payableAmount = calculatePayableAmount(
            props.amount ?? 0,
            commissionRate,
            commissions.bankId,
            commissions.isCommissionFromDealer
          );

          let commissionRateDecimal =
            100 * ((payableAmount - props.amount) / props.amount);

          if (isCommissionIncluded && props.amount !== undefined) {
            payableAmount = props.amount;
          }
          const installmentAmount = payableAmount / totalInstallment;

          const extra =
            commissions.deferringInstallmentCount > 0 ? (
              <span style={{ color: "#9254de", marginLeft: 4 }}>
                ⏳({commissions.deferringInstallmentCount})
              </span>
            ) : commissions.extraInstallmentCount > 0 ? (
              <span className="gx-text-success">{`+(${commissions.extraInstallmentCount})`}</span>
            ) : null;

          const hasDeferringRange =
            commissions.deferringInstallmentCountRangeStartValue &&
            commissions.deferringInstallmentCountRangeEndValue;

          const radioValue = `${commissions.bankId}/${commissions.id}/${
            commissions.cardName ?? commissions.registeredCardName
          }/${i + 1}`;

          return (
            <List.Item key={i}>
              <Radio
                value={radioValue}
                bankId={commissions.bankId}
                installmentCount={i + 1}
                style={{ width: "100%" }}
                commissionDefinitionId={commissions.id}
                cardType={commissions.cardName ?? commissions.registeredCardName}
                disabled={isCommissionDisabled(commissions.id)}
              >
                <span>
                  {i + 1} {extra} x{" "}
                  {ColumnHelper.renderDecimal(installmentAmount)}
                </span>

                {selectedValue === radioValue && (
                  <>
                    <span style={{ fontSize: "small", display: "block" }}>
                      {ColumnHelper.renderDecimal(payableAmount)}{" "}
                      {props.amount > 0 &&
                      props?.hideCommissionRateInfo !== true
                        ? `(% ${ColumnHelper.renderDecimal(
                            commissionRateDecimal
                          )})`
                        : undefined}
                    </span>
                    {hasDeferringRange && (
                      <InputNumber
                        min={
                          commissions.deferringInstallmentCountRangeStartValue
                        }
                        max={commissions.deferringInstallmentCountRangeEndValue}
                        placeholder={`Erteleme (${commissions.deferringInstallmentCountRangeStartValue}-${commissions.deferringInstallmentCountRangeEndValue})`}
                        style={{ width: "100%", marginTop: 8 }}
                        onChange={handleDeferringInstallmentChange}
                      />
                    )}
                  </>
                )}
              </Radio>
            </List.Item>
          );
        })}

        {commissions.description &&
          (!justOneInstallment ||
            (justOneInstallment &&
              Object.keys(commissions).filter(
                (x) =>
                  x.startsWith("commissionRate") &&
                  !x.endsWith("Enabled") &&
                  x !== "commissionRate1" &&
                  commissions[x] != null
              ).length === 0)) && (
            <List.Item key={-2}>{commissions.description}</List.Item>
          )}
      </List>
    );
  };

  const renderTemplateList = (commissions) => {
    const templates = commissions.agricultureCardTemplates;
    if (!templates || templates.length === 0) return null;

    return (
      <List size="small">
        {templates.map((template) => {
          let payableAmount = calculatePayableAmount(
            props.amount ?? 0,
            template.commissionRate,
            commissions.bankId,
            commissions.isCommissionFromDealer
          );

          let commissionRateDecimal =
            100 * ((payableAmount - props.amount) / props.amount);

          const radioValue = `${commissions.bankId}/${commissions.id}/${
            commissions.cardName ?? commissions.registeredCardName
          }/T_${template.id}`;

          return (
            <List.Item key={template.id}>
              <Radio
                value={radioValue}
                bankId={commissions.bankId}
                installmentCount={1}
                style={{ width: "100%" }}
                commissionDefinitionId={commissions.id}
                cardType={commissions.cardName ?? commissions.registeredCardName}
                disabled={isCommissionDisabled(commissions.id)}
              >
                <span>
                  {template.maturityPeriod}
                  {template.paymentFrequency && (
                    <span style={{ color: "#9254de", marginLeft: 4 }}>
                      ⏳({template.paymentFrequency})
                    </span>
                  )}
                  {" x "}
                  {ColumnHelper.renderDecimal(payableAmount)}
                </span>
                {selectedValue === radioValue && (
                  <span style={{ fontSize: "small", display: "block" }}>
                    {ColumnHelper.renderDecimal(payableAmount)}{" "}
                    {props.amount > 0 &&
                    props?.hideCommissionRateInfo !== true
                      ? `(% ${ColumnHelper.renderDecimal(
                          commissionRateDecimal
                        )})`
                      : undefined}
                  </span>
                )}
              </Radio>
            </List.Item>
          );
        })}
      </List>
    );
  };

  const renderKGT = (commissions) => {
    if (commissions.extraInstallmentExpireDate) {
      return (
        <div style={{ textAlign: "center", margin: "10px 0" }}>
          <Tooltip title="Kampanya Geçerlilik Tarihi">
            K.G.T {moment(commissions.extraInstallmentExpireDate).format("L")}
          </Tooltip>
        </div>
      );
    }
  };
  useEffect(() => {
    if (props.data?.commissions) {
      const hasTemplates = (c) => c.agricultureCardTemplates?.length > 0;

      const templateCommissions = props.data?.commissions?.filter(hasTemplates);
      const nonTemplateCommissions = props.data?.commissions?.filter(
        (c) => !hasTemplates(c)
      );

      const visibleInstallmentCommissions = nonTemplateCommissions?.filter(
        (commission) =>
          ![2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].every(
            (num) => commission[`commissionRate${num}`] == null
          )
      );
      setInstallmentOptionsCount(
        visibleInstallmentCommissions.length + (templateCommissions?.length ?? 0)
      );

      const justOneInstallmentOptionsCount = nonTemplateCommissions?.filter(
        (commission) =>
          ![1].every((num) => commission[`commissionRate${num}`] == null)
      );
      setJustOneInstallmentOptionsCount(justOneInstallmentOptionsCount.length);
    }
  }, [props.data]);

  return props.data?.commissions?.length > 0 ? (
    <Form.Item
      name="bankIdInstallment"
      label={
        <div style={{ display: "flex", alignItems: "center" }}>
          <span style={{ marginRight: "10px" }}>{t("paymentOptions")} :</span>
          <B2BFileModal
            filePath={props.filePath}
            visibility={props.campaignButtonVisibility}
          />
        </div>
      }
      colon={false}
      labelCol={{ span: 24, style: { height: "66px" } }}
      rules={[
        {
          required:
            (props.disabledOptions === true && !props.enabledCommissionIds?.length) ||
            (props.enabledCommissionIds != null && props.enabledCommissionIds.length === 0)
              ? false
              : true,
          message: t("paymentOptionsRequiredMessage"),
        },
      ]}
    >
      <Radio.Group
        onChange={onChangeRadio}
        value={props.radioValue}
        style={{ width: "100%" }}
      >
        {justOneInstallmentOptionsCount > 0 && (
          <Collapse accordion={true} defaultActiveKey={["1"]}>
            <Collapse.Panel
              header={t("singlePayment")}
              key="1"
              style={{ border: "none" }}
            >
              <Row
                gutter={[16, 16]}
                justify="center"
                title={t("singlePayment")}
              >
                {props.data?.commissions
                  ?.filter(
                    (commission) =>
                      !(commission.agricultureCardTemplates?.length > 0) &&
                      ![1].every(
                        (num) => commission[`commissionRate${num}`] == null
                      )
                  )
                  .map((commissions) => (
                    <Col xs={24} sm={12} md={8} lg={6} key={commissions.id}>
                      <Card
                        style={{
                          margin: "10px 0",
                          height: "calc(100% - 20px)",
                          opacity: isCommissionDisabled(commissions.id) ? 0.45 : 1,
                        }}
                        className="gx-card"
                        bodyStyle={{ padding: 0 }}
                      >
                        <CardLogo
                          {...commissions}
                          rowStyle={{ marginTop: 5, marginBottom: 5 }}
                          imageHeight={30}
                          allList={props.data?.commissions?.filter(
                            (x) => !x.isVisaMasterCard
                          )}
                        />
                        {renderList(commissions, true)}
                        {/* {renderKGT(commissions)} */}
                      </Card>
                    </Col>
                  ))}
              </Row>
            </Collapse.Panel>
          </Collapse>
        )}

        {installmentOptionsCount > 0 && (
          <Collapse accordion={true} defaultActiveKey={["2"]}>
            <Collapse.Panel
              header={
                <span style={{ color: "red" }}>{t("installmentOptions")}</span>
              }
              key="2"
            >
              <Row gutter={[16, 16]} justify="center">
                {props.data?.commissions
                  ?.filter(
                    (commission) =>
                      (!(commission.agricultureCardTemplates?.length > 0) &&
                        ![2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].every(
                          (num) => commission[`commissionRate${num}`] == null
                        )) ||
                      commission.agricultureCardTemplates?.length > 0
                  )
                  .map((commissions) => (
                    <Col xs={24} sm={12} md={8} lg={6} key={commissions.id}>
                      <Card
                        style={{
                          margin: "10px 0",
                          height: "calc(100% - 20px)",
                          opacity: isCommissionDisabled(commissions.id) ? 0.45 : 1,
                        }}
                        className="gx-card"
                        bodyStyle={{ padding: 0 }}
                      >
                        <CardLogo
                          {...commissions}
                          rowStyle={{ marginTop: 5, marginBottom: 5 }}
                          imageHeight={30}
                          allList={props.data?.commissions?.filter(
                            (x) => !x.isVisaMasterCard
                          )}
                        />
                        {commissions.agricultureCardTemplates?.length > 0 ? (
                          <>
                            {commissions.agricultureCardLogo && (
                              <div style={{ textAlign: "center", padding: "4px 0" }}>
                                <img
                                  src={commissions.agricultureCardLogo}
                                  alt="Tarım Kart"
                                  style={{ height: 20 }}
                                />
                              </div>
                            )}
                            {renderTemplateList(commissions)}
                          </>
                        ) : (
                          <>
                            {renderList(commissions, false)}
                            {renderKGT(commissions)}
                          </>
                        )}
                      </Card>
                    </Col>
                  ))}
              </Row>
            </Collapse.Panel>
          </Collapse>
        )}
      </Radio.Group>
    </Form.Item>
  ) : (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={
        props?.data?.useEmptyCommissionList
          ? "Kartınız için uygun bir ödeme seçeneği bulunmamaktadır."
          : "Ödeme seçenekleri için kart bilgilerinizi giriniz."
      }
    />
  );
};

export default PosSelectableInstallmentTable;
